import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { userToken, executeLaravelAPI, showToastMessage } from '../../Utils';
import { Form } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import ConfirmationModal from '../Confirmation';
import Cookies from 'js-cookie';


const itemsPerPageOptions = [20, 50, 100, 500, 1000];

const DisabledProjectList = ({ show, handleClose }) => {
    const location = useLocation();
    const pageDetails = { 'pagetitle': 'Projects', 'pageslug': 'projects' };

    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageOptions[0]);
    const [paginatedData, setPaginatedData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    const inputActiveRefs = useRef([]);
    const [projectResponseData, setProjectResponseData] = useState([]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [tempProjectDetails, setTempProjectDetails] = useState({ index: null, isChecked: false, projectId: null });
    const [reload, setReload] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const searchTimeoutRef = useRef(null);
    const isInitialMount = useRef(true);

    // Debounce search query to avoid too many API calls
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500); // 500ms debounce delay

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery]);

    // Reset to first page when debounced search query changes (skip initial render)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        setCurrentPage(0);
    }, [debouncedSearchQuery]);

    const getAllParams = (location) => {
        const searchParams = new URLSearchParams(location.search);
        const allParams = {};
        for (const [key, value] of searchParams.entries()) {
            allParams[key] = value;
        }
        allParams.page = currentPage + 1;
        allParams.per_page = itemsPerPage;
        return allParams;
    }

    useEffect(() => {
        document.title = pageDetails.pagetitle;
        const params = getAllParams(location);
        // params.project_status = selectedStatus;
        getProjects(params);
    }, [currentPage, itemsPerPage, reload, debouncedSearchQuery]);


    const getProjects = async (paramsData) => {
        try {
            setLoading(true);
            paramsData.city = Cookies.get("selectedCityCookie") || "pune";
            paramsData.bwusercheck = 1;
            // Remove orderby as it's not needed for deactivated projects
            delete paramsData.orderby;
            
            // Add search parameters if search query exists
            if (debouncedSearchQuery && debouncedSearchQuery.trim() !== '') {
                paramsData.searchtype = 'name';
                paramsData.name = debouncedSearchQuery.trim();
            }
            
            const result = await executeLaravelAPI('deactivatedprojects', paramsData, 'GET', userToken);
            const projectData = result.data.data;
            setProjectResponseData(projectData);
            setPaginatedData(projectData);
            setTotalItems(result.data.total);
            // Initialize refs for toggle switches
            inputActiveRefs.current = Array(projectData.length).fill(null).map((_, index) => inputActiveRefs.current[index] || null);

        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected);
        // Scroll to top of page when page changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleItemsPerPageChange = (e) => {
        const selectedValue = parseInt(e.target.value, 10);
        setItemsPerPage(selectedValue);
        setCurrentPage(0); // Reset to the first page when changing items per page
        // Scroll to top of page when items per page changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        // Note: currentPage reset is handled in the debounce useEffect
    };


    // Helper function to parse RERA IDs
    const parseReraIds = (reraIds) => {
        if (!reraIds) return 'N/A';
        try {
            // rera_ids is a JSON string like "[[\"P52100012200\"]]" or "[[{\"reraNumber\":\"P52100010550\"}]]"
            const parsed = JSON.parse(reraIds);
            if (Array.isArray(parsed) && parsed.length > 0) {
                const innerArray = parsed[0];
                if (Array.isArray(innerArray) && innerArray.length > 0) {
                    // Extract all RERA IDs
                    const reraNumbers = innerArray.map(reraItem => {
                        // Check if it's an object with reraNumber property
                        if (typeof reraItem === 'object' && reraItem.reraNumber) {
                            return reraItem.reraNumber;
                        }
                        // Otherwise it's a string
                        if (typeof reraItem === 'string') {
                            return reraItem;
                        }
                        return null;
                    }).filter(item => item !== null);
                    
                    return reraNumbers.length > 0 ? reraNumbers.join(', ') : 'N/A';
                }
            }
            return 'N/A';
        } catch (error) {
            console.error('Error parsing RERA IDs:', error);
            return 'N/A';
        }
    }

    const handleActiveChange = async (index, isChecked, projectId) => {
        setTempProjectDetails({ index, isChecked, projectId });
        setShowConfirmModal(true);

    };



    const handleConfirm = async () => {
        const { index, isChecked, projectId } = tempProjectDetails;
        setShowConfirmModal(false); // Close the modal
        try {
            let jsondata = {
                "is_active": isChecked ? 1 : 0
            };

            const result = await executeLaravelAPI(`project/${projectId}/activate`, jsondata, 'PUT', userToken);
            inputActiveRefs.current[index].checked = isChecked;

            showToastMessage('Project Status updated', 'top-right', 'success');
            
            // If project is activated, reset to first page to avoid empty page issues
            if (isChecked && currentPage > 0) {
                setCurrentPage(0);
            }
            
            // Refetch the disabled projects list to get updated data from server
            // Construct params manually to ensure correct page number
            const searchParams = new URLSearchParams(location.search);
            const params = {};
            for (const [key, value] of searchParams.entries()) {
                params[key] = value;
            }
            // Use page 1 if we're resetting, otherwise use current page
            params.page = (isChecked && currentPage > 0) ? 1 : currentPage + 1;
            params.per_page = itemsPerPage;
            await getProjects(params);
        } catch (error) {
            console.error('Error handling active change:', error);
            showToastMessage('Error updating project status', 'top-right', 'error');
            // Handle error, show error message, etc.
        }
    };

    const handleCancel = () => {
        setShowConfirmModal(false); // Close the modal without doing anything
    };



    return (
        <>
            <section className="content">
                <div className="row">
                    <div className="col-md-12">
                        <div className="box">
                            <div className="row mb-3">
                                <div className="col-lg-12">
                                    <div className="position-relative">
                                                    <input
                                                        type="search"
                                            className="form-control search-input shadow-none border-none"
                                            placeholder="Search by Project Name"
                                                        value={searchQuery}
                                            onChange={handleSearchChange}
                                            autoComplete="off"
                                            style={{
                                                background: "#ffffff url('https://api.iconify.design/ion/search-outline.svg') no-repeat 10px center / 24px",
                                                height: '48px',
                                                paddingLeft: '44px'
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="box-body table-container mt-4 hr-scroll-style">
                                <table className="table table-resposive table-hover dashboard-table shadow-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th width="200px" style={{ 'minWidth': '200px' }}>Project Name</th>
                                            <th>RERA ID</th>
                                            <th>Location</th>
                                            <th>Developer Name</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan={5}><div className='loading'><span className="loadingspan">Loading...</span><span className="custom-loader"></span></div></td></tr>
                                        ) : (
                                            paginatedData.length > 0 ? (
                                                paginatedData.map((project, index) => (
                                                    <tr key={project.id}>
                                                        <td>{project.name}</td>
                                                        <td>{parseReraIds(project.rera_ids)}</td>
                                                        <td>{project.address?.location || 'N/A'}</td>
                                                        <td>{project.developer?.name || 'N/A'}</td>
                                                        <td>
                                                            <Form.Check
                                                                type="switch"
                                                                id={`custom-switch-${index}`}
                                                                key={index}
                                                                ref={el => inputActiveRefs.current[index] = el}
                                                                name={`active${index}`}
                                                                data-project-id={project.id}
                                                                checked={project.is_active || false}
                                                                onChange={(e) => handleActiveChange(index, e.target.checked, e.target.getAttribute('data-project-id'))}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr><td colSpan={5} className="text-center">No disabled projects found</td></tr>
                                            )
                                        )
                                        }
                                    </tbody>
                                    <tfoot></tfoot>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='row mt-5 align-items-center'>
                    <div className='col-lg-4'>
                        {/* Display "Showing x-y of z items" */}
                        <p className='graylight mb-0'>Showing {currentPage * itemsPerPage + 1}-{Math.min((currentPage + 1) * itemsPerPage, totalItems)} of {totalItems} items</p>
                    </div>
                    <div className='col-lg-8 d-flex justify-content-end align-items-center'>
                        {/* Render pagination component */}
                        <div className='me-3'>
                            <span className='graylight'>Results per page </span>
                            <select className='showPerPage' value={itemsPerPage} onChange={handleItemsPerPageChange}>
                                {itemsPerPageOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <ReactPaginate
                            pageCount={Math.ceil(totalItems / itemsPerPage)}
                            onPageChange={handlePageClick}
                            containerClassName={'pagination justify-content-end mb-0'}
                            pageClassName={'page-item'}
                            pageLinkClassName={'page-link'}
                            activeClassName={'active'}
                            previousClassName={'page-item'}
                            previousLinkClassName={'page-link'}
                            nextClassName={'page-item'}
                            nextLinkClassName={'page-link'}
                            breakClassName={'page-item'}
                            breakLinkClassName={'page-link'}
                            disableInitialCallback={true}
                        />
                    </div>
                </div>

                <ConfirmationModal
                    show={showConfirmModal}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                    modelBody={"Are you sure you want to change the status?"}
                />
            </section>
        </>
    )
};

export default DisabledProjectList;